/**
 * Shared "deploy a residual lump sum" helper — mutates `data.assets` in place and returns the total
 * amount deployed so the caller can route the remainder to the bank.
 * Ported verbatim from frontend/src/calculations/residualDeployment.js.
 */
import { buildAnnuityImpact, annuityAssetFromRecord } from './annuityStrategies.ts';
import { calculateAge } from './support/dateHelpers.ts';
import { newMoneyProfileOf, personalSplitFor, ASSET_CLASS_ASSUMPTIONS, investmentBondNetPctFor } from './investmentProfiles.ts';
import { applyNonConcessionalContributionToAssets } from './nonConcessionalContributions.ts';
import type { FactFind, FactFindAsset, FactFindAssetMap } from './factFindTypes.ts';

export interface ResidualDeployment {
    shares?: number;
    investmentProperty?: number;
    ipRent?: number;
    ipExpenses?: number;
    /**
     * Stamp duty on the bought investment property (2 Oct 2026). Leaves the proceeds beside the
     * price and sits in the property's CGT cost base — the village engine charged it from 24 Sep
     * 2026 on its own path; the downsizer and granny flat deployments never did. Zero where no
     * state was chosen, and the card says so.
     */
    ipStampDuty?: number;
    /** Whose name the property is bought in; the household default (joint for a couple) when absent. */
    ipOwnership?: 'client' | 'partner' | 'joint';
    annuity?: number;
    annuityType?: 'lifetime' | 'fixedTerm' | 'guaranteed';
    annuityResidualPercent?: number;
    annuityTermYears?: number;
    /** Lifetime only: years before the payments start (a deferred lifetime annuity). */
    annuityDeferralYears?: number;
    annuityIndexation?: boolean;
    annuityReversionary?: boolean;
    annuityOwner?: 'client' | 'partner' | 'joint';
    annuityIncomeOverride?: number;
    /**
     * After-tax (non-concessional) super contributions, per person — F.6. Each is CAPPED at what
     * that person may contribute (nonConcessionalContributions.ts); anything above the cap is not
     * deployed and falls to the bank with the rest of the residual.
     */
    superContribution?: number;
    partnerSuperContribution?: number;
    /** Start an account-based pension with the contribution, where the person may (age 60+). */
    superCommenceIncomeStream?: boolean;
    /** An investment (insurance) bond — F.5. Counted and deemed; taxed inside; locked for ten years. */
    investmentBond?: number;
}

const num = (v: number | undefined) => Math.max(0, v || 0);

/** Total $ a deployment config allocates (shares + IP + annuity). Pure; safe for bounds-checking. */
export function totalResidualDeployment(deployment: ResidualDeployment | null | undefined): number {
    if (!deployment) return 0;
    return num(deployment.shares) + num(deployment.investmentProperty) + num(deployment.ipStampDuty) + num(deployment.annuity)
        + num(deployment.superContribution) + num(deployment.partnerSuperContribution) + num(deployment.investmentBond);
}

/**
 * The one shape an investment bond bought by a strategy takes. Priced at the client's new-money
 * profile net of the bond's own 30% tax (investmentBondNetPctFor); starts its ten-year clock at nil.
 */
export function buildInvestmentBondAsset(
    base: FactFind,
    amount: number,
    overrides: { id: string; description?: string; ownership?: string },
): FactFindAsset {
    return {
        id: overrides.id,
        type: 'investmentBonds',
        description: overrides.description ?? 'Investment bond',
        value: amount,
        ownership: overrides.ownership ?? (base?.adviceFor === 'couple' ? 'joint' : 'client'),
        incomeRate: 0,
        growthRate: investmentBondNetPctFor(newMoneyProfileOf(base)),
        costBase: amount,
        __investmentBondYearsHeld: 0,
    } as FactFindAsset;
}

/**
 * Apply a deployment config to a (already-cloned) fact-find's assets, in place.
 *
 * `ctx.factFind` keeps the wire name because this function is called straight off a request body (see
 * `applyResidualDeployment`'s route in calculation.ts) — but it holds a fact-find like every other
 * `factFind` on that boundary, and is typed as one. It is the UNMODIFIED fact-find, read only for
 * `adviceFor` to default the ownership of what gets deployed; `data` is the working clone that is
 * mutated. They are the same shape, which is why omitting it and falling back to `data` is correct.
 */
export function applyResidualDeployment(
    data: FactFind,
    deployment: ResidualDeployment | null | undefined,
    ctx: { factFind?: FactFind; idPrefix?: string; defaultOwnership?: 'joint' | 'client' } = {},
): number {
    if (!deployment) return 0;
    const base = ctx.factFind || data;
    const idPrefix = ctx.idPrefix || 'deploy';
    const isCouple = base?.adviceFor === 'couple';
    const defaultOwnership = ctx.defaultOwnership || (isCouple ? 'joint' : 'client');

    if (!data.assets) data.assets = {};
    let deployed = 0;

    const shares = num(deployment.shares);
    if (shares > 0) {
        if (!data.assets.shares) data.assets.shares = [];
        // Priced at the client's new-money profile (Conservative until they answer).
        const split = personalSplitFor(newMoneyProfileOf(base));
        data.assets.shares.push({
            id: `${idPrefix}-shares`,
            type: 'shares',
            description: 'Shares / managed funds (from proceeds)',
            value: shares,
            ownership: defaultOwnership,
            incomeRate: split.incomeRatePct,
            growthRate: split.growthRatePct,
            costBase: shares,
            frankingCredit: split.frankingPct,
            reinvest: false,
        });
        deployed += shares;
    }

    const ip = num(deployment.investmentProperty);
    if (ip > 0) {
        if (!data.assets.investmentProperty) data.assets.investmentProperty = [];
        data.assets.investmentProperty.push({
            id: `${idPrefix}-inv-prop`,
            type: 'investmentProperty',
            description: 'Investment property (from proceeds)',
            value: ip,
            ownership: deployment.ipOwnership || defaultOwnership,
            rent: num(deployment.ipRent),
            expenses: num(deployment.ipExpenses),
            growthRate: ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct,
            // The duty is money spent on the purchase: gone from the proceeds, kept in the cost base.
            costBase: ip + num(deployment.ipStampDuty),
            acquisitionDate: 'post',
        });
        deployed += ip + num(deployment.ipStampDuty);
    }

    // After-tax super, capped per person at what the rules allow — the cap is applied HERE, so a
    // manual figure above it books the cap and the excess stays in the bank.
    const superClient = num(deployment.superContribution);
    if (superClient > 0) {
        deployed += applyNonConcessionalContributionToAssets(data.assets as FactFindAssetMap, base, {
            amount: superClient,
            ownership: 'client',
            commenceIncomeStream: !!deployment.superCommenceIncomeStream,
            idPrefix: `${idPrefix}-super`,
        });
    }
    const superPartner = isCouple ? num(deployment.partnerSuperContribution) : 0;
    if (superPartner > 0) {
        deployed += applyNonConcessionalContributionToAssets(data.assets as FactFindAssetMap, base, {
            amount: superPartner,
            ownership: 'partner',
            commenceIncomeStream: !!deployment.superCommenceIncomeStream,
            idPrefix: `${idPrefix}-super`,
        });
    }

    const bond = num(deployment.investmentBond);
    if (bond > 0) {
        if (!data.assets.investmentBonds) data.assets.investmentBonds = [];
        data.assets.investmentBonds.push(buildInvestmentBondAsset(base, bond, {
            id: `${idPrefix}-bond`,
            description: 'Investment bond (from proceeds)',
            ownership: defaultOwnership,
        }));
        deployed += bond;
    }

    const annuity = num(deployment.annuity);
    if (annuity > 0) {
        const clientAge = base?.client?.dob ? calculateAge(base.client.dob) : 65;
        const owner = deployment.annuityOwner || defaultOwnership;
        // Honour the ownership choice: a single owner with no reversionary is single-life (no partner age),
        // matching the shared annuity configurator. Joint ownership or a reversionary nomination prices joint-life.
        const wantsContinuation = !!deployment.annuityReversionary || owner === 'joint';
        const partnerAge = isCouple && wantsContinuation && base?.partner?.dob ? calculateAge(base.partner.dob) : null;
        const annuityType = deployment.annuityType === 'fixedTerm' ? 'fixedTerm' : deployment.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime';
        const residualCapital = Math.min(1, Math.max(0, num(deployment.annuityResidualPercent) / 100));
        const termYears = annuityType === 'fixedTerm' ? num(deployment.annuityTermYears) || null : null;
        const deferralYears = annuityType === 'lifetime' ? Math.round(num(deployment.annuityDeferralYears)) : 0;
        const incomeOverride = deployment.annuityIncomeOverride != null ? deployment.annuityIncomeOverride : null;

        const annuityRecord = buildAnnuityImpact(base, {
            id: `${idPrefix}-annuity`,
            annuityType,
            purchaseAmount: annuity,
            age: clientAge,
            partnerAge,
            owner,
            residualCapital,
            termYears,
            deferralYears,
            indexation: !!deployment.annuityIndexation,
            reversionary: !!deployment.annuityReversionary && !!partnerAge,
            incomeOverride: Number.isFinite(incomeOverride) ? incomeOverride : null,
        });

        if (annuityRecord && !annuityRecord.warnings) {
            if (!data.assets.annuities) data.assets.annuities = [];
            const kind = annuityType === 'guaranteed' ? 'Guaranteed' : annuityType === 'lifetime' ? (deferralYears > 0 ? 'Deferred lifetime' : 'Lifetime') : 'Fixed-term';
            data.assets.annuities.push(annuityAssetFromRecord(annuityRecord, {
                id: annuityRecord.id || `${idPrefix}-annuity`,
                description: `${kind} annuity (from proceeds)`,
                ownership: owner,
            }));
            deployed += annuity;
        }
    }

    return deployed;
}

/** Human-readable "where the proceeds go" rows for a deployment (for strategy cards / insight panels). */
export function describeResidualDeployment(deployment: ResidualDeployment | null | undefined): Array<{ label: string; amount: number }> {
    if (!deployment) return [];
    const rows: Array<{ label: string; amount: number }> = [];
    const shares = num(deployment.shares);
    const ip = num(deployment.investmentProperty);
    const annuity = num(deployment.annuity);
    const superClient = num(deployment.superContribution);
    const superPartner = num(deployment.partnerSuperContribution);
    const bond = num(deployment.investmentBond);
    if (shares > 0) rows.push({ label: 'Into shares / managed funds', amount: shares });
    if (ip > 0) rows.push({ label: 'Into an investment property', amount: ip });
    if (ip > 0 && num(deployment.ipStampDuty) > 0) rows.push({ label: 'Stamp duty on the investment property', amount: num(deployment.ipStampDuty) });
    if (superClient > 0) rows.push({ label: `Into super (after-tax contribution${deployment.superCommenceIncomeStream ? ', as a pension' : ''})`, amount: superClient });
    if (superPartner > 0) rows.push({ label: `Into partner's super (after-tax contribution${deployment.superCommenceIncomeStream ? ', as a pension' : ''})`, amount: superPartner });
    if (bond > 0) rows.push({ label: 'Into an investment bond', amount: bond });
    if (annuity > 0) {
        const kind = deployment.annuityType === 'fixedTerm'
            ? `${num(deployment.annuityTermYears) ? `${num(deployment.annuityTermYears)}-year ` : ''}fixed-term`
            : deployment.annuityType === 'guaranteed'
                ? 'guaranteed (full amount to the estate)'
                : (num(deployment.annuityDeferralYears) > 0 ? `deferred (${num(deployment.annuityDeferralYears)} years) lifetime` : 'lifetime');
        rows.push({ label: `Into a ${kind} annuity`, amount: annuity });
    }
    return rows;
}
